import { css } from '@emotion/react';
import { PropsWithChildren, Children } from 'react';
import { getBreakpoint } from './breakpoints';

const rowBreakpoints = (template: Array<string>) => css`
  display: grid;
  justify-content: space-between;
  margin: auto;
  max-width: 100%;

  @media (min-width: ${getBreakpoint('xs')}px) {
    column-gap: 24px;
    grid-template-columns: ${template[0]};
    padding-left: 32px;
    padding-right: 32px;
    width: 375px;
  }

  @media (min-width: ${getBreakpoint('sm')}px) {
    grid-template-columns: ${template[1]};
    width: 768px;
  }

  @media (min-width: ${getBreakpoint('md')}px) {
    grid-template-columns: ${template[2]};
    padding-left: 60px;
    padding-right: 60px;
    width: 768px;
  }

  @media (min-width: ${getBreakpoint('lg')}px) {
    grid-template-columns: ${template[3]};
    width: 1024px;
  }

  @media (min-width: ${getBreakpoint('xl')}px) {
    column-gap: 32px;
    grid-template-columns: ${template[4]};
    width: 1168px;
  }
`;

const LayoutStyles = {
  '12': { style: rowBreakpoints(Array(5).fill('minmax(0, 1fr)')), count: 1 },
  '6-6': {
    style: rowBreakpoints([
      'minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)'
    ]),
    count: 2
  },
  '4-4-4': {
    style: rowBreakpoints([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'repeat(3, minmax(0, 1fr))',
      'repeat(3, minmax(0, 1fr))',
      'repeat(3, minmax(0, 1fr))'
    ]),
    count: 3
  },
  '3-3-3-3': {
    style: rowBreakpoints([
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'repeat(4, minmax(0, 1fr))',
      'repeat(4, minmax(0, 1fr))',
      'repeat(4, minmax(0, 1fr))'
    ]),
    count: 4
  },
  '2-2-2-2-2-2': {
    style: rowBreakpoints([
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr)',
      'repeat(6, minmax(0, 1fr))',
      'repeat(6, minmax(0, 1fr))'
    ]),
    count: 6
  },
  '4-8': {
    style: rowBreakpoints([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 2fr)',
      'minmax(0, 1fr) minmax(0, 2fr)',
      'minmax(0, 1fr) minmax(0, 2fr)'
    ]),
    count: 2
  },
  '3-9': {
    style: rowBreakpoints([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 3fr)',
      'minmax(0, 1fr) minmax(0, 3fr)',
      'minmax(0, 1fr) minmax(0, 3fr)'
    ]),
    count: 2
  },
  '8-4': {
    style: rowBreakpoints([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 2fr) minmax(0, 1fr)',
      'minmax(0, 2fr) minmax(0, 1fr)',
      'minmax(0, 2fr) minmax(0, 1fr)'
    ]),
    count: 2
  },
  '9-3': {
    style: rowBreakpoints([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 3fr) minmax(0, 1fr)',
      'minmax(0, 3fr) minmax(0, 1fr)',
      'minmax(0, 3fr) minmax(0, 1fr)'
    ]),
    count: 2
  },
  '3-3-6': {
    style: rowBreakpoints([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)',
      'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)',
      'minmax(0, 1fr) minmax(0, 1fr) minmax(0, 2fr)'
    ]),
    count: 3
  },
  '6-3-3': {
    style: rowBreakpoints([
      'minmax(0, 1fr)',
      'minmax(0, 1fr)',
      'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)',
      'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)'
    ]),
    count: 3
  }
};

type Entries<T> = {
  [K in keyof T]: [K, T[K]];
}[keyof T][];

const getEntries = <T extends object>(obj: T) =>
  Object.entries(obj) as Entries<T>;

const DefaultStyles = Object.fromEntries(
  getEntries(LayoutStyles)
    .slice(0, 5)
    .map(([key, val]) => [val['count'], key])
);

Object.freeze(LayoutStyles);
Object.freeze(DefaultStyles);

type layout = keyof typeof LayoutStyles;

export interface RowProps {
  layout: layout;
}

const getLayoutStyles = (childCount: number, layout?: layout) => {
  if (layout === undefined) {
    layout = DefaultStyles[childCount];
    if (layout === undefined) {
      throw new Error(`${childCount} is an invalid number of children`);
    }
  }
  if (childCount != LayoutStyles[layout]['count']) {
    throw new Error(
      `Row with layout "${layout}" should have ${LayoutStyles[layout]['count']} children`
    );
  }

  return LayoutStyles[layout]['style'];
};

export const Row = (props: PropsWithChildren<RowProps>) => {
  const { layout } = props;
  const children = Children.toArray(props.children);
  const style = getLayoutStyles(children.length, layout);

  return <div css={style}>{children.map((child) => child)}</div>;
};
